import { useCallback, useRef, useState } from 'react';
import { regenerateQuestion, errMsg } from './aiPaperApi';

const mapQ = (paper, qId, fn) => ({
  ...paper,
  sections: paper.sections.map((s) => ({ ...s, questions: s.questions.map((q) => (q.qId === qId ? fn(q) : q)) }))
});

// All paper-editing operations. Callbacks are stable so memoised question cards don't remount.
export default function usePaperOps(user, paper, setPaper, setMsg) {
  const [busyId, setBusyId] = useState('');
  const paperRef = useRef(paper);
  paperRef.current = paper;

  const onChange = useCallback((qId, field, value) =>
    setPaper((p) => mapQ(p, qId, (q) => ({ ...q, [field]: value }))), [setPaper]);

  const onOptionChange = useCallback((qId, i, field, value) =>
    setPaper((p) => mapQ(p, qId, (q) => ({
      ...q, options: q.options.map((o, j) => (j === i ? { ...o, [field]: value } : o))
    }))), [setPaper]);

  const onMove = useCallback((qId, dir) => setPaper((p) => ({
    ...p,
    sections: p.sections.map((s) => {
      const i = s.questions.findIndex((q) => q.qId === qId);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= s.questions.length) return s;
      const qs = s.questions.slice();
      [qs[i], qs[j]] = [qs[j], qs[i]];
      return { ...s, questions: qs };
    })
  })), [setPaper]);

  const onDelete = useCallback((qId) => {
    if (!window.confirm('Delete this question?')) return;
    setPaper((p) => ({ ...p, sections: p.sections.map((s) => ({ ...s, questions: s.questions.filter((q) => q.qId !== qId) })) }));
  }, [setPaper]);

  const onRegenerate = useCallback(async (qId) => {
    const p = paperRef.current;
    const all = p.sections.flatMap((s) => s.questions);
    const q = all.find((x) => x.qId === qId);
    if (!q) return;
    setBusyId(qId);
    setMsg('');
    try {
      const res = await regenerateQuestion(user, {
        sourceDocId: p.sourceDocId, question: q, languageMode: p.languageMode, subject: p.subject, class: p.class,
        avoid: all.map((x) => x.textEnglish || x.textHindi)
      });
      setPaper((cur) => mapQ(cur, qId, () => res.question));
    } catch (e) { setMsg('❌ ' + errMsg(e)); }
    finally { setBusyId(''); }
  }, [user, setPaper, setMsg]);

  return { busyId, onChange, onOptionChange, onMove, onDelete, onRegenerate };
}
